import { AgentRole, RunStatus, type ActiveRunDto, type HubPlanetDto } from '@aiai/shared';
import type { Bounds } from './camera';

/** SVG viewBox of the solar system. */
export const VIEW = { width: 760, height: 480 } as const;
/** The sun. */
export const CENTER = { x: 380, y: 246 } as const;
/** Orbits are ellipses: vertical radius = horizontal radius × ORBIT_TILT. */
export const ORBIT_TILT = 0.5;
const FIRST_ORBIT = 95;
const ORBIT_GAP = 27;

/** FNV-1a of `seed:n` mapped to [0, 1) — stable across reloads and machines. */
export function seededUnit(seed: string, n: number): number {
  let h = 0x811c9dc5;
  const s = `${seed}:${n}`;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) / 0x100000000;
}

export function planetRadius(openIssueCount: number): number {
  return 6 + 2.4 * Math.sqrt(Math.min(Math.max(openIssueCount, 0), 100));
}

function orbitSpeed(orbit: number): number {
  // radians per ms — inner orbits faster, like a real orrery
  return 0.00022 * Math.pow(FIRST_ORBIT / orbit, 1.5);
}

export function positionOnOrbit(orbit: number, angle: number): { x: number; y: number } {
  return { x: CENTER.x + orbit * Math.cos(angle), y: CENTER.y + orbit * ORBIT_TILT * Math.sin(angle) };
}

export interface PlanetSlot {
  planet: HubPlanetDto;
  orbit: number;
  phase: number;
  speed: number;
  radius: number;
}

/**
 * Every visible project is a planet. Orbits are assigned by AGE (oldest
 * innermost), so a planet keeps its orbit as activity changes; a new project
 * gets the next orbit outward. The universe zooms, so there is no cap.
 */
export function layoutPlanets(planets: HubPlanetDto[]): PlanetSlot[] {
  return [...planets]
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id))
    .map((planet, i) => {
      const orbit = FIRST_ORBIT + i * ORBIT_GAP;
      return {
        planet,
        orbit,
        phase: seededUnit(planet.id, 0) * Math.PI * 2,
        speed: orbitSpeed(orbit),
        radius: planetRadius(planet.openIssueCount),
      };
    });
}

/** The outermost orbit (the first orbit when the sky is empty). */
export function outerOrbit(slots: PlanetSlot[]): number {
  return slots.length ? slots[slots.length - 1].orbit : FIRST_ORBIT;
}

/**
 * Constellations live on a ring this far outside the outermost orbit, in every direction (the
 * ring is the orbit grown by a constant, not scaled), so a figure (±55) never touches an orbit.
 */
const RING_GAP = 150;
/** Room past the ring for a figure's far edge and its name. */
const RING_MARGIN = 75;

function ringRadii(outer: number): { rx: number; ry: number } {
  return { rx: outer + RING_GAP, ry: outer * ORBIT_TILT + RING_GAP };
}

/** Everything the universe draws: orbits + planets + the constellation ring. */
export function worldBounds(slots: PlanetSlot[]): Bounds {
  const { rx, ry } = ringRadii(outerOrbit(slots));
  return {
    minX: CENTER.x - rx - RING_MARGIN, maxX: CENTER.x + rx + RING_MARGIN,
    minY: CENTER.y - ry - RING_MARGIN, maxY: CENTER.y + ry + RING_MARGIN,
  };
}

const RING_SAMPLES = 360;

/**
 * Where each constellation sits: evenly spaced, by arc length, round the ring outside `outer`.
 * Their order round the ring is seeded by project id, so adding a project only nudges its
 * neighbours apart; the gaps stay equal however many there are.
 */
export function constellationAnchors(projectIds: string[], outer: number): Map<string, { x: number; y: number }> {
  const { rx, ry } = ringRadii(outer);
  const at = (t: number) => ({ x: CENTER.x + rx * Math.cos(t), y: CENTER.y + ry * Math.sin(t) });
  // Cumulative arc length at each sampled angle, so a fraction of the way round maps to a point.
  const lengths = [0];
  for (let i = 1; i <= RING_SAMPLES; i++) {
    const a = at(((i - 1) / RING_SAMPLES) * Math.PI * 2);
    const b = at((i / RING_SAMPLES) * Math.PI * 2);
    lengths.push(lengths[i - 1] + Math.hypot(b.x - a.x, b.y - a.y));
  }
  const total = lengths[RING_SAMPLES];
  const pointAt = (fraction: number) => {
    const target = fraction * total;
    let i = 1;
    while (i < RING_SAMPLES && lengths[i] < target) i++;
    const f = (target - lengths[i - 1]) / (lengths[i] - lengths[i - 1] || 1);
    return at(((i - 1 + f) / RING_SAMPLES) * Math.PI * 2);
  };
  const ordered = [...projectIds].sort((a, b) => seededUnit(a, 7) - seededUnit(b, 7) || a.localeCompare(b));
  const start = ordered.length ? seededUnit(ordered[0], 7) : 0;
  return new Map(ordered.map((id, i) => [id, pointAt((start + i / ordered.length) % 1)]));
}

export type AgentTag = 'E' | 'PM';

/**
 * Whether an agent is actually working on this run: started (not queued behind a
 * concurrency or build-lane gate) and not paused awaiting a gate. The one
 * definition the hub uses for anything that moves or counts as "live" — ships,
 * halos, comets, agents live, stop-all, planet ranking.
 */
export function isWorkingRun(run: Pick<ActiveRunDto, 'status' | 'awaitingGate'>): boolean {
  return !run.awaitingGate && run.status !== RunStatus.Pending;
}

/** Engineer for build runs; PM for plan and chat runs (lane first, then stage role). */
export function agentTagForRun(run: Pick<ActiveRunDto, 'lane' | 'currentStageRole'>): AgentTag {
  if (run.lane === 'build') return 'E';
  if (run.lane === 'plan' || run.lane === 'chat') return 'PM';
  return run.currentStageRole === AgentRole.Planner || run.currentStageRole === AgentRole.Chat ? 'PM' : 'E';
}
